import { CheckSquare, Square, Loader2 } from "lucide-react";

const EUR = (v) => (Number(v) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });

const HEAD_CLS = "px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]";

function HeaderRow({ allSelected, toggleAll }) {
  return (
    <thead className="bg-[#F5F2EB] border-b border-[#E6E1D6]">
      <tr>
        <th className="px-3 py-2 w-8">
          <button onClick={toggleAll} className="text-[#0F1829]" data-testid="toggle-all"
            title={allSelected ? "Desmarcar todos" : "Marcar todos"}>
            {allSelected ? <CheckSquare size={15} /> : <Square size={15} />}
          </button>
        </th>
        <th className={`${HEAD_CLS} text-left`}>Cliente</th>
        <th className={`${HEAD_CLS} text-left`}>Email</th>
        <th className={`${HEAD_CLS} text-right`}>Ganhas</th>
        <th className={`${HEAD_CLS} text-right`}>Pago</th>
        <th className={`${HEAD_CLS} text-right`}>Por Pagar</th>
        <th className={`${HEAD_CLS} text-right`}>Disponível</th>
        <th className={`${HEAD_CLS} text-right`}>Valor a Pagar</th>
        <th className={`${HEAD_CLS} text-left`}>Obs. (opcional)</th>
      </tr>
    </thead>
  );
}

function ClientCell({ row }) {
  const tipo = row.TipoCliente || row.ID_TipoCliente;
  return (
    <>
      <span className="font-mono text-[11px] text-[#978D78] mr-1.5">#{row.ID_Cliente}</span>
      <span className="font-medium">{row.NomeCompleto}</span>
      {tipo && (
        <span className="ml-1.5 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#F5F2EB] border border-[#E6E1D6] text-[#6B7280]">
          {tipo}
        </span>
      )}
    </>
  );
}

function PayoutRow({ row, isSel, defaultVal, maxVal, override, onToggle, onChangeValue, onChangeObs }) {
  const valueShown = override.valor !== undefined ? override.valor : defaultVal;
  const numericValue = Number(valueShown);
  // Validação client-side (espelha backend): valor a pagar nunca pode exceder
  // o que o consultor tem disponível para receber neste momento.
  const exceedsMax = isSel && Number.isFinite(numericValue) && Number.isFinite(maxVal) && numericValue > maxVal + 0.005;
  const invalidZero = isSel && Number.isFinite(numericValue) && numericValue <= 0;
  const hasError = exceedsMax || invalidZero;
  return (
    <tr className={`border-b border-[#F3F0E7] ${isSel ? "bg-[#FAF8F2]" : "hover:bg-[#FAF8F2]/60"}`}
      data-testid={`row-${row.ID_Cliente}`}>
      <td className="px-3 py-2">
        <button onClick={onToggle} className="text-[#0F1829]" data-testid={`toggle-${row.ID_Cliente}`}>
          {isSel ? <CheckSquare size={15} /> : <Square size={15} />}
        </button>
      </td>
      <td className="px-3 py-2 whitespace-nowrap"><ClientCell row={row} /></td>
      <td className="px-3 py-2 whitespace-nowrap text-[#4B5563]">{row.Email || "—"}</td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{EUR(row.ValorTotal)}</td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{EUR(row.ValorPago)}</td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{EUR(row.PorPagar)}</td>
      <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{EUR(row.DisponivelPagamento)}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <input type="number" step="0.01" min="0.01" max={maxVal} value={valueShown}
          onChange={onChangeValue} disabled={!isSel}
          aria-invalid={hasError}
          className={`w-24 px-2 py-1 text-xs font-mono border rounded text-right disabled:bg-[#FAF8F2] disabled:text-[#9CA3AF] outline-none ${
            hasError
              ? "border-red-400 bg-red-50 focus:border-red-600"
              : "border-[#E6E1D6] focus:border-[#0F1829]"
          }`}
          data-testid={`value-${row.ID_Cliente}`}
          data-invalid={hasError ? "true" : "false"}
          title={exceedsMax ? `Máx. ${EUR(maxVal)}` : (invalidZero ? "Valor deve ser > 0" : "")}
        />
        {exceedsMax && (
          <p className="text-[10px] text-red-600 mt-0.5 text-right" data-testid={`value-error-${row.ID_Cliente}`}>
            Máx. {EUR(maxVal)}
          </p>
        )}
        {invalidZero && !exceedsMax && (
          <p className="text-[10px] text-red-600 mt-0.5 text-right" data-testid={`value-error-${row.ID_Cliente}`}>
            Valor &gt; 0
          </p>
        )}
      </td>
      <td className="px-3 py-2">
        <input type="text" value={override.obs || ""}
          onChange={onChangeObs} disabled={!isSel} placeholder="(usa default)"
          className="w-48 px-2 py-1 text-xs border border-[#E6E1D6] rounded disabled:bg-[#FAF8F2] disabled:text-[#9CA3AF] outline-none focus:border-[#0F1829]"
          data-testid={`obs-${row.ID_Cliente}`} />
      </td>
    </tr>
  );
}

export default function PayoutTable({ rows, loading, selected, overrides, targetCol, toggle, toggleAll, setOverride }) {
  const allSelected = selected.size > 0 && selected.size === rows.length;
  return (
    <div className="bg-white border border-[#E6E1D6] rounded-md overflow-x-auto">
      <table className="text-sm" style={{ minWidth: "100%", width: "max-content" }}>
        <HeaderRow allSelected={allSelected} toggleAll={toggleAll} />
        <tbody>
          {loading && (
            <tr><td colSpan={9} className="text-center py-8 text-[#6B7280]">
              <Loader2 size={16} className="inline animate-spin mr-1" /> A carregar...
            </td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={9} className="text-center py-8 text-[#6B7280]">
              Sem consultores a pagar com estes filtros.
            </td></tr>
          )}
          {!loading && rows.map((r) => {
            const isSel = selected.has(r.ID_Cliente);
            const override = overrides[r.ID_Cliente] || {};
            const defaultVal = Number(r[targetCol] || 0).toFixed(2);
            const maxVal = Number(r[targetCol] || 0);
            return (
              <PayoutRow key={r.ID_Cliente}
                row={r} isSel={isSel} defaultVal={defaultVal} maxVal={maxVal} override={override}
                onToggle={() => toggle(r.ID_Cliente)}
                onChangeValue={(e) => setOverride(r.ID_Cliente, "valor", e.target.value)}
                onChangeObs={(e) => setOverride(r.ID_Cliente, "obs", e.target.value)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
